import { print } from 'graphql';

import { MUTATION_REFRESH_TOKENS } from './mutations/auth';
import { refreshCoordinator } from 'utils/refreshCoordinator';

let refreshPromise: Promise<void> | null = null;

const doRefresh = async (): Promise<void> => {
    const uri = process.env.REACT_APP_GRAPHQL_URL;
    if (!uri) throw new Error('GraphQL URL is not configured');

    const res = await fetch(uri, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            operationName: 'RefreshTokens',
            query: print(MUTATION_REFRESH_TOKENS)
        })
    });

    if (!res.ok) throw new Error(`RefreshTokens failed: ${res.status}`);

    const json = (await res.json()) as { data?: { refreshTokens?: unknown }; errors?: unknown[] };
    if (json.errors?.length || json.data?.refreshTokens == null) {
        throw new Error('RefreshTokens returned errors');
    }
};

export const refreshAuthTokens = (): Promise<void> => {
    // Same-tab dedup: reuse the in-flight promise if one exists.
    if (refreshPromise) return refreshPromise;

    // Cross-tab coordination: if another tab already holds the refresh lock,
    // wait for its broadcast instead of firing a second rotation against the
    // same HttpOnly refresh cookie (which would produce "Refresh token already used").
    if (refreshCoordinator.isAnotherTabRefreshing()) {
        return refreshCoordinator
            .waitForBroadcast()
            .then(() => undefined)
            .catch(() => undefined); // swallow — caller checks result independently
    }

    refreshCoordinator.acquireLock();
    refreshPromise = doRefresh()
        .then(() => refreshCoordinator.broadcastDone())
        .catch((err: unknown) => {
            refreshCoordinator.broadcastFailed((err as Error)?.message ?? 'Refresh failed');
            throw err;
        })
        .finally(() => {
            refreshPromise = null;
            refreshCoordinator.releaseLock();
        });

    return refreshPromise;
};
